/**
 * Share-Variants for the Story-Output page bottom-sheet.
 *
 * Source-of-truth is `webflow/rating_and_share_button.js` v8 (production
 * copy, lines 62-103). Literals, emoji, German typographic quotes, UTM
 * parameters, line breaks, and ternary fallbacks are replicated verbatim
 * — any rewrite would invalidate legally-reviewed brand copy and A/B-
 * tested conversion phrasing.
 *
 * Quote-character asymmetry in the source is intentional and preserved:
 *   - `proud` WhatsApp text uses German typographic quotes („ + ")
 *     (U+201E opening, U+201C closing) per brand typography convention.
 *   - `wissensblatt` / `neugier` WhatsApp texts use straight ASCII
 *     double-quotes (").
 *   - All `emailBody` fields use straight quotes consistently across
 *     every variant.
 * Do NOT normalize.
 *
 * Share URL target is the marketing homepage (`dreamable.kids`), not the
 * authenticated PWA story URL. Matches Webflow: optimises viral funnel,
 * avoids recipient-side auth wall.
 */

export const DREAMABLE_URL = 'https://dreamable.kids';
const NO_CHILD_FALLBACK = 'mein Kind';

export type ShareMode = 'standard' | 'sb_explanation';

export type ShareVariantKey = 'proud' | 'screenfree' | 'tired' | 'wissensblatt' | 'neugier';

export interface ShareVariant {
  key: ShareVariantKey;
  emoji: string;
  label: string;
  whatsappText: string;
  emailSubject: string;
  emailBody: string;
  copyUrl: string;
}

export function buildVariants(
  childName: string | null,
  storyTitle: string | null,
  mode: ShareMode,
): ShareVariant[] {
  const n = (childName ?? '').trim();
  const t = (storyTitle ?? '').trim();
  const d = n || NO_CHILD_FALLBACK;

  if (mode === 'sb_explanation') {
    return [buildWissensblatt(d, t), buildNeugier(d, t)];
  }
  return [buildProud(d, t), buildScreenfree(d), buildTired()];
}

// ── Standard mode builders ─────────────────────────────────────

function buildProud(d: string, t: string): ShareVariant {
  return {
    key: 'proud',
    emoji: '📖',
    label: 'Geschichte teilen',
    whatsappText:
      (t ? '„' + t + '" — ' : '') +
      'Eine Geschichte nur für ' +
      d +
      ', mit echtem Namen, echten Orten und echten Freunden! 🌟\n' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=whatsapp&utm_content=proud&utm_campaign=story_share',
    emailSubject: (t ? t + ' — ' : '') + 'Eine personalisierte Geschichte für ' + d + '! 🌟',
    emailBody:
      'Hallo!\n\n' +
      (t ? 'Wir haben gerade "' + t + '" erstellt — ' : 'Wir haben gerade ') +
      'eine personalisierte Geschichte, in der ' +
      d +
      ' der Held ist.\n\nDreamable erstellt einzigartige Geschichten mit echtem Namen, echten Orten und echten Freunden deines Kindes.\n\nProbier es kostenlos aus: ' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=email&utm_content=proud&utm_campaign=story_share\n\nViele Grüße!',
    copyUrl:
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=copy&utm_content=proud&utm_campaign=story_share',
  };
}

function buildScreenfree(d: string): ShareVariant {
  return {
    key: 'screenfree',
    emoji: '📵',
    label: 'Bildschirmfrei-Tipp teilen',
    whatsappText:
      'Wir haben eine Alternative zu Bildschirmzeit entdeckt: personalisierte Hörgeschichten, in denen ' +
      d +
      ' der Held ist. Bildschirmfrei & jede Geschichte ist einzigartig ✨\n' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=whatsapp&utm_content=screenfree&utm_campaign=story_share',
    emailSubject: 'Bildschirmfrei-Tipp: Personalisierte Hörgeschichten',
    emailBody:
      'Hallo!\n\nWir haben eine tolle Alternative zu Bildschirmzeit entdeckt: Dreamable erstellt personalisierte Hörgeschichten, in denen ' +
      d +
      ' der Held ist.\n\nKomplett bildschirmfrei und jede Geschichte ist einzigartig.\n\nProbier es kostenlos aus: ' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=email&utm_content=screenfree&utm_campaign=story_share\n\nViele Grüße!',
    copyUrl:
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=copy&utm_content=screenfree&utm_campaign=story_share',
  };
}

function buildTired(): ShareVariant {
  return {
    key: 'tired',
    emoji: '😅',
    label: 'Eltern-Tipp teilen',
    whatsappText:
      'Wenn dein Kind auch zum 100. Mal dieselbe Geschichte hören will: Dreamable erstellt jedes Mal eine neue, personalisierte Geschichte mit deinem Kind als Held. Echte Rettung für müde Eltern 😅\n' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=whatsapp&utm_content=tired&utm_campaign=story_share',
    emailSubject: 'Nie wieder dieselbe Geschichte 100x vorlesen 😅',
    emailBody:
      'Hallo!\n\nKennst du das? Zum hundertsten Mal dieselbe Geschichte vorlesen?\n\nDreamable erstellt jedes Mal eine NEUE, personalisierte Geschichte — mit dem Namen, den Orten und den Interessen deines Kindes.\n\nProbier es kostenlos aus: ' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=email&utm_content=tired&utm_campaign=story_share\n\nViele Grüße!',
    copyUrl:
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=copy&utm_content=tired&utm_campaign=story_share',
  };
}

// ── SB-Kurzerklärung mode builders ─────────────────────────────

function buildWissensblatt(d: string, t: string): ShareVariant {
  return {
    key: 'wissensblatt',
    emoji: '🧠',
    label: 'Wissensblatt teilen',
    whatsappText:
      (t ? '"' + t + '" — ' : '') +
      'Schlauberger hat es ' +
      d +
      ' erklärt — mit Wow-Fakt und Probier-Tipp! 🦊\n' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=whatsapp&utm_content=wissensblatt&utm_campaign=story_share',
    emailSubject: (t ? t + ' — ' : '') + 'Schlauberger hat es ' + d + ' erklärt! 🦊',
    emailBody:
      'Hallo!\n\n' +
      (t ? 'Wir haben gerade "' + t + '" erklärt bekommen — ' : '') +
      'Schlauberger erklärt Kindern die Welt, mit Wow-Fakt und Probier-Tipp zum Nachmachen!\n\nProbier es kostenlos aus: ' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=email&utm_content=wissensblatt&utm_campaign=story_share\n\nViele Grüße!',
    copyUrl:
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=copy&utm_content=wissensblatt&utm_campaign=story_share',
  };
}

function buildNeugier(d: string, t: string): ShareVariant {
  return {
    key: 'neugier',
    emoji: '💡',
    label: 'Neugier-Tipp teilen',
    whatsappText:
      d +
      ' wollte wissen: ' +
      (t ? '"' + t + '"' : 'eine spannende Frage') +
      '. Dreamable erklärt Kinderfragen kindgerecht — mit Wow-Fakt und Experiment! 🔬\n' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=whatsapp&utm_content=neugier&utm_campaign=story_share',
    emailSubject: 'Kinderfragen kindgerecht beantwortet 💡',
    emailBody:
      'Hallo!\n\n' +
      d +
      ' wollte wissen: ' +
      (t ? '"' + t + '"' : 'eine spannende Frage') +
      '.\n\nDreamable erklärt Kinderfragen kindgerecht — mit Wow-Fakt und Experiment zum Nachmachen.\n\nProbier es kostenlos aus: ' +
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=email&utm_content=neugier&utm_campaign=story_share\n\nViele Grüße!',
    copyUrl:
      DREAMABLE_URL +
      '?utm_source=share&utm_medium=copy&utm_content=neugier&utm_campaign=story_share',
  };
}
